import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, ImagePlus, X } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { toast } from '@/app/components/ui/simple-toaster';
import { useReportAssetProblem } from '@/hooks/use-assets';
import { handleApiError } from '@/lib/api-client';
import type { Asset, AssetStatus } from '@/lib/types';

const OUT_OF_SERVICE_STATUSES: AssetStatus[] = ['quarantined', 'damaged', 'retired', 'lost'];
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png'];
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

interface ReportProblemDialogProps {
  asset: Pick<Asset, 'id' | 'display_name' | 'serial_number' | 'status'>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReportProblemDialog({ asset, open, onOpenChange }: ReportProblemDialogProps) {
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const reportMutation = useReportAssetProblem();

  const alreadyOutOfService = OUT_OF_SERVICE_STATUSES.includes(asset.status);

  const photoPreview = useMemo(
    () =>
      photo && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(photo) : null,
    [photo],
  );

  useEffect(
    () => () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    },
    [photoPreview],
  );

  const reset = () => {
    setDescription('');
    setPhoto(null);
    setError(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && reportMutation.isPending) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handlePhotoSelected = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      setError('Photo must be a JPEG or PNG image.');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setError('Photo must be 10 MB or smaller.');
      return;
    }
    setError(null);
    setPhoto(file);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = description.trim();
    if (!trimmed) {
      setError('Describe the problem before submitting.');
      return;
    }
    setError(null);
    reportMutation.mutate(
      { assetId: asset.id, description: trimmed, photo },
      {
        onSuccess: (data) => {
          toast.success(
            data.report.status_changed
              ? 'Problem reported. Gear is now quarantined.'
              : 'Problem reported. Admins and Managers have been alerted.',
          );
          reset();
          onOpenChange(false);
        },
        onError: (err) => {
          setError(handleApiError(err).message);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>Report a problem</DialogTitle>
            <DialogDescription>
              {asset.display_name} · {asset.serial_number}
            </DialogDescription>
          </DialogHeader>

          <p className="text-sm text-gray-600">
            {alreadyOutOfService
              ? `This gear is already ${asset.status}. Its status won't change, but Admins and Managers will be alerted.`
              : 'This gear will be marked Quarantined and taken out of service until an inspector releases it. Admins and Managers will be alerted.'}
          </p>

          <div className="space-y-2">
            <Label htmlFor="problem-description">What's wrong? (required)</Label>
            <Textarea
              id="problem-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Core shot near the end, sheath cut, gate won't close"
              rows={4}
              maxLength={5000}
              required
              aria-required="true"
              className="text-base"
            />
          </div>

          <div className="space-y-2">
            <Label>Photo (optional)</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-12"
                onClick={() => cameraInputRef.current?.click()}
              >
                <Camera className="h-5 w-5 mr-2" />
                Take photo
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-12"
                onClick={() => uploadInputRef.current?.click()}
              >
                <ImagePlus className="h-5 w-5 mr-2" />
                Upload photo
              </Button>
            </div>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/jpeg,image/png"
              capture="environment"
              className="hidden"
              aria-label="Take photo with camera"
              onChange={(e) => {
                handlePhotoSelected(e.target.files);
                e.target.value = '';
              }}
            />
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              aria-label="Choose photo to upload"
              onChange={(e) => {
                handlePhotoSelected(e.target.files);
                e.target.value = '';
              }}
            />
            {photo && (
              <div className="flex items-center gap-3 rounded-md border p-2">
                {photoPreview && (
                  <img
                    src={photoPreview}
                    alt="Selected problem photo"
                    className="h-16 w-16 rounded object-cover"
                  />
                )}
                <span className="flex-1 truncate text-sm">{photo.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remove photo"
                  onClick={() => setPhoto(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-12 sm:h-10"
              onClick={() => handleOpenChange(false)}
              disabled={reportMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              className="h-12 sm:h-10"
              disabled={reportMutation.isPending}
            >
              {reportMutation.isPending ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
